/**
 * Quote modal — handles open/close, variant tracking, file encoding,
 * Turnstile token collection, and CRM API submission via proxy.
 *
 * Setup required in quote-modal.liquid:
 *   data-proxy-url           — URL of your Cloudflare Worker proxy
 *   data-turnstile-site-key  — Cloudflare Turnstile site key (public)
 *
 * Setup required in snippets/product.quote-buttons-wrapper.liquid ([data-quote-buttons]):
 *   data-section-id          — Shopify section ID (for variant:change event)
 *   data-product-id          — Shopify product ID (for variant:change event)
 *   data-crm-product-id      — CRM imprintable ID from metafield
 *
 * A single QuoteModal instance serves every [data-quote-buttons] wrapper on the
 * page (e.g. many product cards in a collection grid). Each open reads its
 * product/variant data from the wrapper closest to the clicked trigger button,
 * not from a single global container — see _openModal / _getButtonsContainer.
 */

(function () {
  'use strict';

  var THROTTLE_KEY = 'aat_quote_submissions';
  var THROTTLE_LIMIT = 5;
  var THROTTLE_WINDOW_MS = 30 * 60 * 1000;

  function QuoteModal(container) {
    this.container = container;
    this.form = document.getElementById('QuoteForm');
    if (!this.form) return;

    this.proxyUrl = this.form.dataset.proxyUrl;
    this.turnstileSiteKey = this.form.dataset.turnstileSiteKey;

    this.submitBtn = this.form.querySelector('[data-quote-submit]');
    this.submitText = this.form.querySelector('[data-quote-submit-text]');
    this.spinner = this.form.querySelector('[data-quote-spinner]');
    this.errorEl = this.form.querySelector('[data-quote-error]');
    this.successEl = this.form.querySelector('[data-quote-success]');
    this.turnstileEl = this.form.querySelector('[data-quote-turnstile]');

    // Move modal to <body> so its z-index isn't constrained by parent stacking contexts
    document.body.appendChild(this.container);

    this._activeBtns = null;
    this._submitted = false;
    this._initModal();
    this._initTurnstile();
    this._trackVariant();
    this._initDecorations();
    this._initFileUploads();
    this.form.addEventListener('submit', this._onSubmit.bind(this));
  }

  // ── Modal open/close ──────────────────────────────────────────────────────

  QuoteModal.prototype._initModal = function () {
    var self = this;

    document.querySelectorAll('.js-modal-open-quote-now').forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        evt.stopPropagation();
        self._openModal(evt.currentTarget);
      });
    });

    this.container.querySelectorAll('[data-modal-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self._closeModal(); });
    });

    document.addEventListener('keyup', function (evt) {
      if (evt.key === 'Escape' && self.container.classList.contains('modal--is-active')) self._closeModal();
    });

    this.container.addEventListener('click', function (evt) {
      if (!evt.target.closest('.modal__inner')) {
        evt.stopPropagation();
        self._closeModal();
      }
    });
  };

  QuoteModal.prototype._openModal = function (triggerBtn) {
    this._activeBtns = (triggerBtn && triggerBtn.closest('[data-quote-buttons]')) || this._getButtonsContainer();

    var nameEl = this.container.querySelector('[data-quote-product-name]');
    if (nameEl) nameEl.textContent = this._activeBtns ? (this._activeBtns.dataset.productTitle || '') : '';

    if (!this._submitted) {
      var minutesLeft = this._checkThrottle();
      if (minutesLeft !== null) {
        this._showThrottleMessage(minutesLeft);
      } else {
        this._clearThrottleMessage();
      }
    }
    this.container.classList.add('modal--is-active');
    document.documentElement.classList.add('modal-open');
  };

  QuoteModal.prototype._closeModal = function () {
    var self = this;
    this.container.classList.add('modal--is-closing');
    this.container.classList.remove('modal--is-active');
    document.documentElement.classList.remove('modal-open');
    setTimeout(function () {
      self.container.classList.remove('modal--is-closing');
    }, 500);
  };

  // ── Throttle ──────────────────────────────────────────────────────────────

  QuoteModal.prototype._checkThrottle = function () {
    try {
      var now = Date.now();
      var subs = JSON.parse(localStorage.getItem(THROTTLE_KEY) || '[]');
      var recent = subs.filter(function (t) { return now - t < THROTTLE_WINDOW_MS; });
      if (recent.length < THROTTLE_LIMIT) return null;
      var oldest = Math.min.apply(null, recent);
      return Math.ceil((THROTTLE_WINDOW_MS - (now - oldest)) / 60000);
    } catch (e) {
      return null;
    }
  };

  QuoteModal.prototype._recordSubmission = function () {
    try {
      var now = Date.now();
      var subs = JSON.parse(localStorage.getItem(THROTTLE_KEY) || '[]');
      subs = subs.filter(function (t) { return now - t < THROTTLE_WINDOW_MS; });
      subs.push(now);
      localStorage.setItem(THROTTLE_KEY, JSON.stringify(subs));
    } catch (e) {}
  };

  QuoteModal.prototype._showThrottleMessage = function (minutesLeft) {
    if (this.form) this.form.hidden = true;

    var existing = this.container.querySelector('[data-throttle-message]');
    if (existing) {
      existing.querySelector('[data-throttle-time]').textContent =
        minutesLeft + ' minute' + (minutesLeft !== 1 ? 's' : '');
      existing.hidden = false;
      return;
    }

    var el = document.createElement('p');
    el.className = 'quote-throttle';
    el.setAttribute('data-throttle-message', '');
    el.innerHTML =
      'To prevent spam, quote requests are limited to ' + THROTTLE_LIMIT + ' per 30 minutes. ' +
      'Please try again in <strong data-throttle-time>' +
      minutesLeft + ' minute' + (minutesLeft !== 1 ? 's' : '') +
      '</strong>.';

    var content = this.container.querySelector('.modal__centered-content');
    if (content) content.appendChild(el);
  };

  QuoteModal.prototype._clearThrottleMessage = function () {
    var el = this.container.querySelector('[data-throttle-message]');
    if (el) el.hidden = true;
    if (this.form) this.form.hidden = false;
  };

  // ── Turnstile ─────────────────────────────────────────────────────────────

  QuoteModal.prototype._initTurnstile = function () {
    if (!this.turnstileSiteKey || !this.turnstileEl) return;

    if (!document.getElementById('cf-turnstile-script')) {
      var script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    this.turnstileEl.setAttribute('data-sitekey', this.turnstileSiteKey);
    this.turnstileEl.classList.add('cf-turnstile');
  };

  // ── Variant tracking ──────────────────────────────────────────────────────

  // Returns the buttons container tied to whichever "Quote Now" trigger opened
  // the modal. Falls back to the first one in the document for safety (e.g. if
  // the modal is ever opened programmatically without a trigger element).
  QuoteModal.prototype._getButtonsContainer = function () {
    return this._activeBtns || document.querySelector('[data-quote-buttons]');
  };

  // Keeps each product's own [data-quote-buttons] wrapper in sync with its
  // variant picker. Bound per-instance (not just the first one on the page)
  // so this works correctly when multiple products' quote buttons exist at
  // once, e.g. a collection grid.
  QuoteModal.prototype._trackVariant = function () {
    document.querySelectorAll('[data-quote-buttons]').forEach(function (btns) {
      var sectionId = btns.dataset.sectionId;
      var productId = btns.dataset.productId;

      document.addEventListener('variant:change:' + sectionId + ':' + productId, function (evt) {
        if (evt.detail && evt.detail.variant) {
          btns.dataset.selectedVariant = String(evt.detail.variant.id);
        }
      });
    });
  };

  QuoteModal.prototype._getColorId = function () {
    var btns = this._getButtonsContainer();
    if (!btns) return '';
    try {
      var map = JSON.parse(btns.dataset.variantColorMap || '{}');
      return map[btns.dataset.selectedVariant] || '';
    } catch (e) {
      return '';
    }
  };

  QuoteModal.prototype._getColorName = function () {
    var btns = this._getButtonsContainer();
    if (!btns) return '';
    try {
      var map = JSON.parse(btns.dataset.variantColorNameMap || '{}');
      return map[btns.dataset.selectedVariant] || '';
    } catch (e) {
      return '';
    }
  };

  QuoteModal.prototype._getProductId = function () {
    var btns = this._getButtonsContainer();
    return btns ? btns.dataset.crmProductId : '';
  };

  // ── File attachments ──────────────────────────────────────────────────────

  QuoteModal.prototype._initFileUploads = function () {
    this._files = [];
    this.filesInput = this.form.querySelector('[data-quote-files-input]');
    this.filesList  = this.form.querySelector('[data-quote-files-list]');
    if (!this.filesInput || !this.filesList) return;

    var self = this;

    this.filesInput.addEventListener('change', function () {
      Array.prototype.forEach.call(self.filesInput.files, function (file) {
        var dup = self._files.some(function (f) {
          return f.name === file.name && f.size === file.size && f.lastModified === file.lastModified;
        });
        if (!dup) self._files.push(file);
      });
      // Clear the native input so the same file can be re-selected after removal
      self.filesInput.value = '';
      self._renderFileList();
    });

    this.filesList.addEventListener('click', function (evt) {
      var btn = evt.target.closest('[data-remove-file]');
      if (!btn) return;
      self._files.splice(parseInt(btn.dataset.removeFile, 10), 1);
      self._renderFileList();
    });
  };

  QuoteModal.prototype._renderFileList = function () {
    if (!this.filesList) return;
    this.filesList.hidden = this._files.length === 0;
    this.filesList.innerHTML = this._files.map(function (f, i) {
      return '<li class="quote-files__item">' +
        '<span class="quote-files__name">' + _esc(f.name) + '</span>' +
        '<span class="quote-files__size">' + _formatBytes(f.size) + '</span>' +
        '<button type="button" class="quote-files__remove" data-remove-file="' + i + '" aria-label="Remove ' + _esc(f.name) + '">&times;</button>' +
        '</li>';
    }).join('');
  };

  // ── UI helpers ────────────────────────────────────────────────────────────

  QuoteModal.prototype._setLoading = function (loading) {
    this.submitBtn.disabled = loading;
    this.submitText.hidden = loading;
    this.spinner.hidden = !loading;
    this.spinner.setAttribute('aria-hidden', String(!loading));
  };

  QuoteModal.prototype._showError = function (msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
    this.successEl.hidden = true;
  };

  QuoteModal.prototype._showSuccess = function (summary) {
    var content = this.container.querySelector('.modal__centered-content');
    if (!content) return;

    var header = content.querySelector('.quote-modal__header');
    if (header) header.hidden = true;
    if (this.form) this.form.hidden = true;

    var fields = [
      ['Product',      summary.product],
      ['Name',         summary.name],
      ['Email',        summary.email],
      ['Organization', summary.organization],
      ['Phone',        summary.phone],
      ['Quantity',     summary.quantity],
      ['Date needed',  summary.date ? _formatDate(summary.date) : ''],
      ['Attached files', summary.files]
    ].filter(function (f) { return !!f[1]; });

    var dlRows = fields.map(function (f) {
      return '<div class="quote-success__row">' +
        '<dt class="quote-success__dt">' + _esc(f[0]) + '</dt>' +
        '<dd class="quote-success__dd">' + _esc(f[1]) + '</dd>' +
        '</div>';
    }).join('');

    var descHtml = summary.description
      ? '<div class="quote-success__section">' +
          '<p class="quote-success__section-label">Your message</p>' +
          '<p class="quote-success__message">' + _esc(summary.description) + '</p>' +
        '</div>'
      : '';

    var decsHtml = '';
    if (summary.decorations && summary.decorations.length) {
      var items = summary.decorations.map(function (d) {
        var detail = d.type === 'Print'
          ? (d.colors ? d.colors + ' color' + (parseInt(d.colors, 10) !== 1 ? 's' : '') : '')
          : d.type === 'Embroidery'
            ? (d.size ? d.size + '"' : '')
            : '';
        return '<li class="quote-success__dec-item">' +
          _esc(d.type) + ' &ndash; ' + _esc(d.location) +
          (detail ? ' &ndash; ' + _esc(detail) : '') +
          '</li>';
      }).join('');
      decsHtml = '<div class="quote-success__section">' +
        '<p class="quote-success__section-label">Decorations</p>' +
        '<ul class="quote-success__dec-list">' + items + '</ul>' +
        '</div>';
    }

    var el = document.createElement('div');
    el.className = 'quote-success';
    el.innerHTML =
      '<div class="quote-success__icon" aria-hidden="true">✓</div>' +
      '<h2 class="quote-success__title">Quote Request Submitted!</h2>' +
      '<p class="quote-success__subtitle">We\'ve received your request and will be in touch shortly.</p>' +
      '<dl class="quote-success__summary">' + dlRows + '</dl>' +
      descHtml + decsHtml;

    content.appendChild(el);
  };

  // ── Decorations ───────────────────────────────────────────────────────────

  QuoteModal.prototype._initDecorations = function () {
    var addBtn = this.container.querySelector('[data-add-decoration]');
    var list = this.container.querySelector('[data-decorations-list]');
    if (!addBtn || !list) return;
    this._decorationsList = list;
    this._decorationCount = 0;
    addBtn.addEventListener('click', function () {
      list.appendChild(this._createDecorationRow());
    }.bind(this));
  };

  QuoteModal.prototype._createDecorationRow = function () {
    this._decorationCount = (this._decorationCount || 0) + 1;
    var num = this._decorationCount;
    var el = document.createElement('div');
    el.className = 'quote-decoration';
    el.setAttribute('data-decoration', '');
    el.innerHTML =
      '<div class="quote-decoration__header">' +
        '<span class="quote-decoration__title">Decoration ' + num + '</span>' +
        '<button type="button" class="quote-decoration__remove" data-remove-decoration>Remove</button>' +
      '</div>' +
      '<div class="quote-decoration__grid">' +
        '<div class="quote-form__field">' +
          '<label class="quote-form__label">Type</label>' +
          '<select class="quote-form__input quote-form__select" data-decoration-type>' +
            '<option value="">Select type…</option>' +
            '<option value="Print">Print / Screen Print</option>' +
            '<option value="Embroidery">Embroidery</option>' +
          '</select>' +
        '</div>' +
        '<div class="quote-form__field">' +
          '<label class="quote-form__label">Location</label>' +
          '<select class="quote-form__input quote-form__select" data-decoration-location>' +
            '<option value="">Select location…</option>' +
            '<option value="Front">Front</option>' +
            '<option value="Back">Back</option>' +
            '<option value="Right Sleeve">Right Sleeve</option>' +
            '<option value="Left Sleeve">Left Sleeve</option>' +
            '<option value="Other">Other</option>' +
          '</select>' +
        '</div>' +
        '<div class="quote-form__field" data-decoration-other-field hidden>' +
          '<label class="quote-form__label">Specify location</label>' +
          '<input type="text" class="quote-form__input" data-decoration-location-text placeholder="e.g. Right chest">' +
        '</div>' +
        '<div class="quote-form__field" data-decoration-colors-field hidden>' +
          '<label class="quote-form__label">How many colors in your art?</label>' +
          '<input type="number" class="quote-form__input" data-decoration-colors min="1" placeholder="e.g. 3">' +
        '</div>' +
        '<div class="quote-form__field quote-form__field--full" data-decoration-size-field hidden>' +
          '<label class="quote-form__label">Art size</label>' +
          '<div class="quote-decoration__size-row">' +
            '<input type="number" class="quote-form__input" data-decoration-width min="0.1" step="0.1" placeholder="W (in)">' +
            '<span class="quote-decoration__size-sep">×</span>' +
            '<input type="number" class="quote-form__input" data-decoration-height min="0.1" step="0.1" placeholder="H (in)">' +
          '</div>' +
        '</div>' +
      '</div>';

    var typeSelect     = el.querySelector('[data-decoration-type]');
    var locationSelect = el.querySelector('[data-decoration-location]');
    var otherField     = el.querySelector('[data-decoration-other-field]');
    var colorsField    = el.querySelector('[data-decoration-colors-field]');
    var sizeField      = el.querySelector('[data-decoration-size-field]');

    typeSelect.addEventListener('change', function () {
      colorsField.hidden = typeSelect.value !== 'Print';
      sizeField.hidden   = typeSelect.value !== 'Embroidery';
    });

    locationSelect.addEventListener('change', function () {
      otherField.hidden = locationSelect.value !== 'Other';
    });

    el.querySelector('[data-remove-decoration]').addEventListener('click', function () {
      el.remove();
    });

    return el;
  };

  QuoteModal.prototype._collectDecorations = function () {
    if (!this._decorationsList) return [];
    var decorations = [];
    this._decorationsList.querySelectorAll('[data-decoration]').forEach(function (row) {
      var type = row.querySelector('[data-decoration-type]').value;
      if (!type) return;

      var locationSelect = row.querySelector('[data-decoration-location]');
      var location = locationSelect.value === 'Other'
        ? (row.querySelector('[data-decoration-location-text]').value || '')
        : locationSelect.value;
      if (!location) return;

      var decoration = { type: type, location: location };

      if (type === 'Print') {
        var colors = row.querySelector('[data-decoration-colors]').value;
        if (colors) decoration.colors = colors;
      } else if (type === 'Embroidery') {
        var w = row.querySelector('[data-decoration-width]').value;
        var h = row.querySelector('[data-decoration-height]').value;
        if (w && h) decoration.size = w + 'x' + h;
      }

      decorations.push(decoration);
    });
    return decorations;
  };

  // ── Turnstile token ───────────────────────────────────────────────────────

  QuoteModal.prototype._getTurnstileToken = function () {
    var input = this.form.querySelector('[name="cf-turnstile-response"]');
    return input ? input.value : '';
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  QuoteModal.prototype._onSubmit = async function (evt) {
    evt.preventDefault();
    this.errorEl.hidden = true;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    if (!this.proxyUrl) {
      this._showError('Quote submission is not configured yet. Please contact us directly.');
      return;
    }

    var turnstileToken = this._getTurnstileToken();
    if (this.turnstileSiteKey && !turnstileToken) {
      this._showError('Please complete the security check.');
      return;
    }

    this._setLoading(true);
    var succeeded = false;

    try {
      var data = new FormData(this.form);
      var files = this._files || [];

      var fromPageSlug = window.location.pathname.replace(/^\//, '');
      var attribution = window.AATAttribution;

      var quoteRequest = {
        name: data.get('name'),
        email: data.get('email'),
        organization: data.get('organization'),
        approx_quantity: data.get('approx_quantity'),
        date_needed: data.get('date_needed'),
        description: data.get('description'),
        phone_number: data.get('phone_number'),
        ip_address: null,
        domain: window.location.hostname,
        source: 'shopify-www',
        campaign_codes: attribution ? attribution.campaignCodes() : '',
        from_page_slug: fromPageSlug,
        job_description: [
          {
            quantity: data.get('approx_quantity'),
            decorations: this._collectDecorations(),
            imprintables: [
              {
                imprintable_id: this._getProductId(),
                color_id: this._getColorId(),
                color_name: this._getColorName()
              }
            ]
          }
        ],
        quote_request_imprintables_attributes: [
          {
            imprintable_id: this._getProductId(),
            color_id: this._getColorId(),
            color_name: this._getColorName(),
            quantity: data.get('approx_quantity')
          }
        ]
      };
      // utm_* / click IDs from the visitor's original campaign landing
      if (attribution) Object.assign(quoteRequest, attribution.quoteFields());

      var body = new FormData();
      body.append('quote_request', JSON.stringify(quoteRequest));
      if (turnstileToken) body.append('turnstile_token', turnstileToken);
      files.forEach(function (file) {
        body.append('customer_uploads[]', file, file.name);
      });

      var controller = new AbortController();
      var timeoutMs = files.length ? 120000 : 30000;
      var timeoutId = setTimeout(function () { controller.abort(); }, timeoutMs);

      // No Content-Type header — the browser sets multipart/form-data with boundary
      var res = await fetch(this.proxyUrl, {
        method: 'POST',
        body: body,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        var body = {};
        try { body = await res.json(); } catch (e) {}
        throw new Error(body.error || 'Server error (' + res.status + ')');
      }

      await res.json();
      this._recordSubmission();
      this._submitted = true;

      var productName = (this.container.querySelector('.quote-modal__product-name') || {}).textContent || '';

      var freshworks = window.AATFreshworks;
      if (freshworks) {
        freshworks.identify({
          name:         data.get('name'),
          email:        data.get('email'),
          phone:        data.get('phone_number'),
          organization: data.get('organization')
        });
        freshworks.trackEvent('Quote requested', data.get('email'), {
          form:        'Product quote',
          products:    productName.trim(),
          quantity:    data.get('approx_quantity'),
          date_needed: data.get('date_needed'),
          page:        window.location.pathname
        });
      }

      // Publish a successful quote submission for analytics integrations.
      // Guarded so an analytics failure can never surface as a submission error.
      try {
        if (window.Shopify && window.Shopify.analytics &&
            typeof window.Shopify.analytics.publish === 'function') {
          var analyticsData = { form_type: 'product_quote', item_count: 1 };
          var analyticsQty  = parseInt(data.get('approx_quantity'), 10);
          if (isFinite(analyticsQty)) analyticsData.quantity = analyticsQty;
          var published = window.Shopify.analytics.publish('quote_submitted', analyticsData);
          if (published && typeof published.catch === 'function') published.catch(function () {});
        }
      } catch (e) {}

      this._showSuccess({
        product:      productName,
        name:         data.get('name'),
        email:        data.get('email'),
        organization: data.get('organization'),
        phone:        data.get('phone_number'),
        quantity:     data.get('approx_quantity'),
        date:         data.get('date_needed'),
        description:  data.get('description'),
        decorations:  this._collectDecorations(),
        files:        files.map(function (f) { return f.name; }).join(', ')
      });
      succeeded = true;

    } catch (err) {
      // Turnstile tokens are single-use: reset so a retry gets a fresh token
      if (window.turnstile) { try { window.turnstile.reset(); } catch (e) {} }
      var msg = err.name === 'AbortError'
        ? 'Request timed out. Please try again.'
        : (err.message || 'Something went wrong. Please try again or contact us directly.');
      this._showError(msg);
    } finally {
      this._setLoading(false);
      if (succeeded) {
        this.form.querySelectorAll('input, textarea, select').forEach(function (el) {
          el.disabled = true;
        });
        this.submitBtn.disabled = true;
      }
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  function _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function _formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  function _formatDate(str) {
    try {
      return new Date(str + 'T12:00:00').toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch (e) {
      return str;
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    var container = document.querySelector('[data-quote-modal]');
    if (container) new QuoteModal(container);
  });
})();
